import { showConfirm } from './tgDialogs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetchJson, apiUploadFormData } from './api';
import {
  fileEmoji,
  formatBytes,
  canPreviewInApp,
  openExternalUrl,
  buildFilePreview,
  type FilePreviewState
} from './fileOpen';
import { FilePreviewModal } from './FilePreviewModal';

type PortfolioFile = {
  id: number;
  original_name: string;
  mime_type?: string | null;
  size_bytes: number;
  created_at?: string;
};

type UploadProgress = {
  done: number;
  total: number;
  currentName?: string;
  percent: number;
};

function uploadOverallPercent(p: UploadProgress): number {
  if (p.total <= 0) return 0;
  const filePart = Math.max(0, Math.min(100, p.percent)) / 100;
  return Math.min(100, Math.round(((p.done + filePart) / p.total) * 100));
}

function ProgressBar({ percent, label }: { percent: number; label?: string }) {
  const indeterminate = percent < 0;
  const width = indeterminate ? 35 : Math.max(2, Math.min(100, percent));
  return (
    <div style={{ marginBottom: 8 }}>
      {label ? (
        <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--fc-accent)', lineHeight: 1.35 }}>{label}</p>
      ) : null}
      <div
        style={{
          height: 10,
          borderRadius: 999,
          background: 'var(--fc-card-hover)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            borderRadius: 999,
            background: 'linear-gradient(90deg, var(--fc-accent), var(--fc-accent))',
            transition: indeterminate ? 'none' : 'width 0.15s ease-out',
            animation: indeterminate ? 'fc-upload-indeterminate 1.1s ease-in-out infinite' : undefined
          }}
        />
      </div>
      {!indeterminate ? (
        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--fc-accent)', textAlign: 'right' }}>{percent}%</p>
      ) : null}
    </div>
  );
}

export default function ServicePortfolioPanel({
  serviceId,
  serviceTitle,
  token,
  canUpload,
  onClose
}: {
  serviceId: number;
  serviceTitle?: string;
  token: string;
  canUpload: boolean;
  onClose: () => void;
}) {
  const [files, setFiles] = useState<PortfolioFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [maxFiles, setMaxFiles] = useState(10);
  const [maxMb, setMaxMb] = useState(200);
  const [preview, setPreview] = useState<FilePreviewState | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { response: r, data } = await apiFetchJson(`/api/executor-services/${serviceId}/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(String(data.error || 'Не удалось загрузить портфолио'));
      setFiles(Array.isArray(data.files) ? (data.files as PortfolioFile[]) : []);
      if (data.max_files != null) setMaxFiles(Number(data.max_files) || 10);
      if (data.max_file_mb != null) setMaxMb(Number(data.max_file_mb) || 200);
    } catch (e: any) {
      setError(e?.message || 'Ошибка');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [serviceId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function uploadFiles(list: FileList | File[]) {
    const arr = Array.from(list || []).slice(0, Math.max(0, maxFiles - files.length));
    if (!arr.length) {
      setError(`Максимум ${maxFiles} файлов`);
      return;
    }
    setBusy(true);
    setError(null);
    setUploadProgress({ done: 0, total: arr.length, currentName: arr[0]?.name, percent: 0 });
    try {
      for (let i = 0; i < arr.length; i++) {
        const file = arr[i];
        setUploadProgress({ done: i, total: arr.length, currentName: file.name, percent: 0 });
        const fd = new FormData();
        fd.append('portfolio', file);
        const { response: r, data } = await apiUploadFormData(
          `/api/executor-services/${serviceId}/portfolio`,
          fd,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeoutMs: 300000,
            onProgress: (ev) => {
              setUploadProgress({
                done: i,
                total: arr.length,
                currentName: file.name,
                percent: ev.percent < 0 ? 0 : ev.percent
              });
            }
          }
        );
        if (!r.ok) throw new Error(String(data.error || `Не удалось загрузить ${file.name}`));
        setUploadProgress({ done: i + 1, total: arr.length, currentName: file.name, percent: 100 });
      }
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setBusy(false);
      setUploadProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function openFile(file: PortfolioFile) {
    setDownloadingId(file.id);
    setError(null);
    try {
      const { response: r, data } = await apiFetchJson(
        `/api/executor-services/${serviceId}/portfolio/${file.id}/download-link`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!r.ok) throw new Error(String(data.error || 'Не удалось открыть файл'));
      const url = String(data.download_url || '');
      if (!url) throw new Error('Пустая ссылка');
      if (canPreviewInApp(file.original_name, file.mime_type)) {
        const built = await buildFilePreview(
          url,
          file.original_name,
          file.mime_type,
          file.size_bytes
        );
        if (built === 'external') openExternalUrl(url);
        else setPreview(built);
      } else {
        openExternalUrl(url);
      }
    } catch (e: any) {
      setError(e?.message || 'Ошибка скачивания');
    } finally {
      setDownloadingId(null);
    }
  }

  async function removeFile(file: PortfolioFile) {
    if (!await showConfirm(`Удалить «${file.original_name}»?`)) return;
    setBusy(true);
    try {
      const { response: r, data } = await apiFetchJson(
        `/api/executor-services/${serviceId}/portfolio/${file.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!r.ok) throw new Error(String(data.error || 'Не удалось удалить'));
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка');
    } finally {
      setBusy(false);
    }
  }

  const totalBytes = files.reduce((s, f) => s + (Number(f.size_bytes) || 0), 0);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Портфолио услуги" className="fc-order-files-cloud">
      <div className="fc-order-files-cloud__scroll">
        <div className="fc-order-files-cloud__topbar">
          <button type="button" className="fc-back" aria-label="Назад" onClick={onClose}>
            ←
          </button>
          <div className="fc-order-files-cloud__title-wrap">
            <h2 className="fc-order-files-cloud__title">☁️ Портфолио</h2>
            <p className="fc-order-files-cloud__subtitle">{serviceTitle || `Услуга #${serviceId}`}</p>
          </div>
          <button type="button" className="fc-btn fc-btn--dark fc-order-files-cloud__close" onClick={onClose}>
            ✕
          </button>
        </div>

        <p className="fc-order-files-cloud__hint">
          Все файлы услуги в одной папке. Картинки, PDF, Word и Excel открываются внутри приложения.
          До {maxMb} МБ на файл, до {maxFiles} файлов.
        </p>

        {error ? <p className="fc-order-files-cloud__error">{error}</p> : null}

        {uploadProgress ? (
          <div className="fc-order-files-cloud__progress">
            <ProgressBar
              percent={uploadOverallPercent(uploadProgress)}
              label={
                uploadProgress.total > 1
                  ? `Файл ${Math.min(uploadProgress.done + 1, uploadProgress.total)}/${uploadProgress.total}: ${
                      uploadProgress.currentName || ''
                    }`
                  : uploadProgress.currentName || 'Загрузка…'
              }
            />
          </div>
        ) : null}

        <section
          style={{
            marginBottom: 16,
            padding: 12,
            background: 'var(--fc-card)',
            borderRadius: 12,
            border: '1px solid var(--fc-border)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>📁</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Портфолио услуги</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                {canUpload ? 'Ваши образцы работ для заказчиков.' : 'Материалы исполнителя.'}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--fc-text-muted)' }}>
                {loading
                  ? 'Загрузка…'
                  : files.length === 0
                    ? 'Папка пуста'
                    : `${files.length} файл(ов) · ${formatBytes(totalBytes)}`}
              </p>
            </div>
          </div>

          {!loading && files.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: canUpload ? 10 : 0 }}>
              {files.map((f) => (
                <div
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => void openFile(f)}
                    disabled={downloadingId === f.id || Boolean(uploadProgress)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flex: 1,
                      minWidth: 0,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid rgba(var(--fc-accent-rgb), 0.2)',
                      background: 'var(--fc-card-hover)',
                      color: 'var(--fc-text)',
                      cursor: downloadingId === f.id ? 'wait' : 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit'
                    }}
                  >
                    <span style={{ fontSize: 22, flexShrink: 0 }}>
                      {fileEmoji(f.original_name, f.mime_type)}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {downloadingId === f.id ? 'Открываю…' : f.original_name}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--fc-text-muted)' }}>
                        {formatBytes(f.size_bytes)}
                        {canPreviewInApp(f.original_name, f.mime_type) ? ' · превью' : ''}
                      </span>
                    </span>
                    <span style={{ color: 'var(--fc-accent)', fontSize: 18, flexShrink: 0 }}>
                      {canPreviewInApp(f.original_name, f.mime_type) ? '👁' : '⬇'}
                    </span>
                  </button>
                  {canUpload ? (
                    <button
                      type="button"
                      className="fc-btn fc-btn--ghost fc-btn--sm"
                      disabled={busy}
                      onClick={() => void removeFile(f)}
                      aria-label="Удалить"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {canUpload ? (
            <>
              <input
                ref={inputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.length) void uploadFiles(e.target.files);
                }}
              />
              <button
                type="button"
                className="fc-btn fc-btn--primary"
                style={{ width: '100%' }}
                disabled={busy || files.length >= maxFiles}
                onClick={() => inputRef.current?.click()}
              >
                {busy
                  ? `Загрузка… ${uploadProgress ? `${uploadOverallPercent(uploadProgress)}%` : ''}`
                  : `➕ Добавить файлы (${files.length}/${maxFiles})`}
              </button>
            </>
          ) : null}
        </section>
      </div>

      {preview ? <FilePreviewModal preview={preview} onClose={() => setPreview(null)} /> : null}
    </div>,
    document.body
  );
}
